import { useState, type FormEvent, type ReactNode } from "react"
import { Link, useSearchParams } from "react-router-dom"
import { toast } from "sonner"

import { cn } from "@/lib/utils"
import { NewPasswordFields } from "@/components/new-password-fields"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field"
import { Input } from "@/components/ui/input"
import { useAuth } from "@/hooks/use-auth"
import { t } from "@/i18n/pl"
import { ApiError } from "@/services/api-client"
import { forgotPassword, resetPassword } from "@/services/auth.service"

type View = "login" | "forgot" | "reset"

const COPY: Record<View, { title: string; description: string; submit: string; busy: string }> = {
  login: {
    title: t.auth.signInTitle,
    description: t.auth.signInDescription,
    submit: t.auth.signIn,
    busy: t.auth.signingIn,
  },
  forgot: {
    title: t.auth.forgotTitle,
    description: t.auth.forgotDescription,
    submit: t.auth.sendCode,
    busy: t.auth.sendingCode,
  },
  reset: {
    title: t.auth.resetTitle,
    description: t.auth.resetDescription,
    submit: t.auth.resetSubmit,
    busy: t.auth.settingPassword,
  },
}

function TextButton({ onClick, children }: { onClick: () => void; children: ReactNode }) {
  return (
    <button type="button" className="underline underline-offset-2" onClick={onClick}>
      {children}
    </button>
  )
}

// Based on the shadcn "login-01" block, reduced to what this product has:
// accounts are provisioned by IT (username may be an email, a handle, or just
// a name), so there is no sign-up or OAuth. "Forgot password" is two steps on
// this same card: a code is e-mailed, then code + new password replace the old
// one — deliberately unlike the settings form, which asks for the current one.
// The e-mail's button opens the code step directly via /login?kod=1.
export function LoginForm({
  className,
  onSuccess,
  ...props
}: React.ComponentProps<"div"> & { onSuccess?: () => void }) {
  const { signIn } = useAuth()
  const [params] = useSearchParams()

  const [view, setView] = useState<View>(params.has("kod") ? "reset" : "login")
  const [username, setUsername] = useState("")
  const [password, setPassword] = useState("")
  const [code, setCode] = useState("")
  const [newPassword, setNewPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  // Only the e-mail carries over between steps; secrets never do.
  function switchView(next: View) {
    setView(next)
    setPassword("")
    setCode("")
    setNewPassword("")
    setConfirmPassword("")
    setError(null)
    setNotice(null)
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setError(null)
    setNotice(null)

    if (view === "reset" && newPassword.length < 12) {
      setError(t.auth.passwordMinLength)
      return
    }
    if (view === "reset" && newPassword !== confirmPassword) {
      setError(t.auth.passwordsDontMatch)
      return
    }

    setSubmitting(true)
    try {
      if (view === "forgot") {
        await forgotPassword(username)
        switchView("reset")
        setNotice(t.auth.codeSent)
      } else if (view === "reset") {
        const chosen = newPassword
        await resetPassword(username, code, chosen)
        // The password is set whatever happens next: a failed sign-in lands
        // on the login view with the e-mail filled in, not on a spent code.
        switchView("login")
        await signIn(username, chosen)
        toast.success(t.auth.passwordChanged)
        onSuccess?.()
      } else {
        await signIn(username, password)
        onSuccess?.()
      }
    } catch (caught) {
      // The API returns one generic message for every credential failure, by
      // design. Show it verbatim instead of guessing at a more specific cause.
      setError(caught instanceof ApiError ? caught.message : t.common.genericError)
    } finally {
      setSubmitting(false)
    }
  }

  const copy = COPY[view]
  // Which field the destructive ring points at: the repeat field for the two
  // client-side password checks, otherwise the credential being checked.
  const passwordError = error === t.auth.passwordMinLength || error === t.auth.passwordsDontMatch
  const credentialError = error !== null && !passwordError

  return (
    <div className={cn("flex flex-col gap-6", className)} {...props}>
      <Card>
        <CardHeader>
          <CardTitle>{copy.title}</CardTitle>
          <CardDescription>{copy.description}</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={(event) => void handleSubmit(event)}>
            <FieldGroup>
              <Field>
                <FieldLabel htmlFor="username">{t.auth.usernameIsEmail}</FieldLabel>
                <Input
                  id="username"
                  type="text"
                  placeholder={t.auth.usernameEmailPlaceholder}
                  autoComplete="username"
                  autoCapitalize="none"
                  required
                  value={username}
                  onChange={(event) => setUsername(event.target.value)}
                  disabled={submitting}
                />
              </Field>
              {view === "login" && (
                <Field>
                  <FieldLabel htmlFor="password">{t.auth.password}</FieldLabel>
                  <Input
                    id="password"
                    type="password"
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    disabled={submitting}
                    aria-invalid={credentialError}
                  />
                </Field>
              )}
              {view === "reset" && (
                <>
                  <Field>
                    <FieldLabel htmlFor="reset-code">{t.auth.resetCode}</FieldLabel>
                    <Input
                      id="reset-code"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      placeholder={t.auth.resetCodePlaceholder}
                      required
                      value={code}
                      onChange={(event) => setCode(event.target.value)}
                      disabled={submitting}
                      aria-invalid={credentialError}
                    />
                  </Field>
                  <NewPasswordFields
                    newPassword={newPassword}
                    confirmPassword={confirmPassword}
                    onNewPasswordChange={setNewPassword}
                    onConfirmPasswordChange={setConfirmPassword}
                    disabled={submitting}
                    invalid={passwordError}
                  />
                </>
              )}
              {error && (
                <FieldDescription role="alert" className="text-destructive">
                  {error}
                </FieldDescription>
              )}
              {notice && <FieldDescription role="status">{notice}</FieldDescription>}
              <Field>
                <Button type="submit" className="h-11" disabled={submitting}>
                  {submitting ? copy.busy : copy.submit}
                </Button>
                <FieldDescription className="text-center">
                  {view === "login" && (
                    <>
                      <TextButton onClick={() => switchView("forgot")}>
                        {t.auth.forgotPassword}
                      </TextButton>
                      <br />
                      {t.auth.noAccount}{" "}
                      <Link to="/rejestracja" className="underline underline-offset-2">
                        {t.auth.requestAccess}
                      </Link>
                    </>
                  )}
                  {view !== "login" && (
                    <>
                      {view === "forgot" ? (
                        <TextButton onClick={() => switchView("reset")}>
                          {t.auth.haveCode}
                        </TextButton>
                      ) : (
                        <TextButton onClick={() => switchView("forgot")}>
                          {t.auth.resendCode}
                        </TextButton>
                      )}
                      <br />
                      <TextButton onClick={() => switchView("login")}>
                        {t.auth.backToSignIn}
                      </TextButton>
                    </>
                  )}
                </FieldDescription>
              </Field>
            </FieldGroup>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
